import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Box, Grid, RadioGroup } from '@mui/material';
import { PriorityHigh } from '@mui/icons-material';
import { object, string, boolean } from 'yup';
import dayjs from 'dayjs';
import {
  modeOfStudyOptions,
  programmeOptions,
  programmeTypeOptions,
  universityCategoryOptions,
} from './options';
import {
  PROGRAMME_NAME,
  PROGRAMME_TYPE,
  STUDY_MODE,
  UNIVERSITY_CATEGORY,
  PROGRAMME_PLAN_STATUS,
} from '..';
import { PAGE_TEXT } from '../lang';
import {
  returnedComponent,
  type InputTextProps,
} from '../../returnFieldComponent';
import {
  type ProgrammeChoiceFormData,
  useGetSupervisorAndArea,
  useGetDepartmentsProgrammes,
} from '../../../graphql';
import {
  Alert,
  Button,
  type SelectOption,
  Loading,
  Radio,
  IconButton,
} from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';
import { getOptionsValue } from '../../../../Monitor/components';

export const checkIsPartnerPartShown = (
  programmeType: PROGRAMME_TYPE | '',
): boolean => {
  return (
    programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME ||
    programmeType === PROGRAMME_TYPE.COLLABORATIVE_PHD_DEGREE_PROGRAMME
  );
};

export type ProgrammeChoiceFormProps = {
  initialValues: ProgrammeChoiceFormData;
  enableEdit: boolean;
  onSubmit: (values: ProgrammeChoiceFormData) => void;
  options: {
    departmentOptions: SelectOption[];
    entryYearsOptions: SelectOption[];
    rpgJoinPhdPartnerList: SelectOption[];
    prgCollabPhdPartnerList: SelectOption[];
  };
};

const PROGRAMME = PAGE_TEXT.PROGRAMME;

const SupervisorTipsLabel = (
  <span>
    {PROGRAMME.PROPOSED_CHIEF_SUPERVISOR}
    <IconButton
      title={PROGRAMME.PROPOSED_SUPERVISOR_TIPS}
      icon={
        <PriorityHigh
          sx={{
            color: '#FFFFFF',
            backgroundColor: '#951D1D',
            '&:hover': { backgroundColor: '#951D1D' },
            borderRadius: '50%',
            width: '0.5em',
            height: '0.5em',
          }}
        />
      }
    />
  </span>
);

export const ProgrammeChoiceForm: React.FunctionComponent<
  ProgrammeChoiceFormProps
> = (props: ProgrammeChoiceFormProps) => {
  const {
    initialValues,
    enableEdit,
    onSubmit,
    options: {
      entryYearsOptions,
      rpgJoinPhdPartnerList,
      prgCollabPhdPartnerList,
    },
  } = props;

  const formik = useFormik<ProgrammeChoiceFormData>({
    initialValues,
    validationSchema: object({
      entryYear: string().required('Required'),
      studyMode: string().required('Required'),
      department: string().required('Required'),
      programme: string().required('Required'),
      programmeType: string().when(
        'isDualOrCollaborative',
        ([isDualOrCollaborative], schema) => {
          return (isDualOrCollaborative as boolean)
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      acceptNoResearchIndicator: boolean().required(),
      researchArea: string().required('Required'),
      proposedChiefSupervisor: string().required('Required'),
      partner: object().when(['isDualOrCollaborative'], {
        is: true,
        then: schema =>
          schema.shape({
            university: string().required('Required'),
            universityCategory: string().when(
              '$programmeType',
              ([programmeType], schema) => {
                return programmeType ===
                  PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME
                  ? schema.required('Required')
                  : schema.notRequired();
              },
            ),
            proposedChiefSupervisor: string().required('Required'),
          }),
        otherwise: schema =>
          schema.shape({
            university: string().notRequired(),
            universityCategory: string().notRequired(),
            proposedChiefSupervisor: string().notRequired(),
          }),
      }),
    }),
    onSubmit,
    enableReinitialize: true,
  });

  const location = useParams();

  const {
    researchAreasAndSupervisors,
    loading: supervisorLoading,
    error: supervisorError,
  } = useGetSupervisorAndArea({
    variables: {
      department: formik.values.department,
      entryYear: formik.values.entryYear,
    },
    skip: !formik.values.department || !formik.values.entryYear,
  });

  const {
    departmentsProgrammes,
    loading: departmentLoading,
    error: departmentError,
  } = useGetDepartmentsProgrammes(formik.values.entryYear);

  const loading = supervisorLoading || departmentLoading;

  const defaultNoSupervisorOption: SelectOption = {
    label: PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION,
    value: PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION,
  };

  const departmentOptions: SelectOption[] = departmentsProgrammes.map(
    department => {
      const programmePlanStatus = department?.plan
        ?.status as PROGRAMME_PLAN_STATUS;
      const applicationDeadline: Record<PROGRAMME_PLAN_STATUS, string> = {
        [PROGRAMME_PLAN_STATUS.Closed]: 'Closed',
        [PROGRAMME_PLAN_STATUS.InProgress]: dayjs(
          department?.plan?.applicationDeadline,
        ).format('DD/MM/YYYY'),
        [PROGRAMME_PLAN_STATUS.NotStarted]: 'Not Started',
      };

      return {
        value: department?.abbr,
        label:
          department?.name +
          ' (Application Deadline: ' +
          applicationDeadline[programmePlanStatus] +
          ')',
        disabled: programmePlanStatus !== PROGRAMME_PLAN_STATUS.InProgress,
      };
    },
  );

  const filteredProgrammeOptions: SelectOption[] = useMemo(() => {
    const department = departmentsProgrammes.find(
      department => department.abbr === formik.values.department,
    );
    return (
      department?.plan.programmes?.map(programme => {
        return {
          value: getOptionsValue(programmeOptions, programme.progLevel),
          label: programme.progLevel,
        };
      }) ?? []
    );
  }, [
    departmentsProgrammes,
    formik.values.entryYear,
    formik.values.department,
  ]);

  const filteredStudyModeOptions: SelectOption[] = useMemo(() => {
    const department = departmentsProgrammes.find(
      department => department.abbr === formik.values.department,
    );
    const programme = department?.plan.programmes.find(
      programme =>
        getOptionsValue(programmeOptions, programme.progLevel) ===
        formik.values.programme,
    );
    return (
      programme?.progMode?.map(programme => ({
        value: getOptionsValue(modeOfStudyOptions, programme),
        label: programme,
      })) ?? []
    );
  }, [
    departmentsProgrammes,
    formik.values.entryYear,
    formik.values.department,
    formik.values.programme,
  ]);

  const researchAreaOptions: SelectOption[] =
    researchAreasAndSupervisors?.researchAreas.map((area: string) => ({
      value: area,
      label: `${area}`,
    })) ?? [];

  const proposedChiefSupervisorOptionGroup: {
    proposedChiefSupervisorOptions: SelectOption[];
    proposedChiefSupervisorBackupOptions: SelectOption[];
  } = useMemo(() => {
    const newOptions = [
      ...(researchAreasAndSupervisors?.proposedSupervisors ?? []),
    ]?.sort((a, b) => a.label?.localeCompare(b.label));
    return {
      proposedChiefSupervisorOptions: [defaultNoSupervisorOption].concat(
        newOptions,
      ),
      proposedChiefSupervisorBackupOptions: newOptions,
    };
  }, [researchAreasAndSupervisors?.proposedSupervisors]);

  const emptyPartnerFields = {
    university: '',
    proposedChiefSupervisor: '',
    universityCategory: '',
    isPolyUHomeUniversity: false,
    physicalLocation: '',
    partnerUniversityApplicationNo: '',
  };

  const handleChangeEntryYear = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      department: '',
      programme: '',
      studyMode: '',
      researchArea: '',
      proposedChiefSupervisor: '',
      researchAreaBackup: '',
      proposedChiefSupervisorBackup: '',
    });
    formik.handleChange(e);
    await formik.setTouched({
      ...formik.touched,
      entryYear: true,
      department: false,
      programme: false,
      studyMode: false,
      researchArea: false,
      proposedChiefSupervisor: false,
      researchAreaBackup: false,
      proposedChiefSupervisorBackup: false,
    });
    formik.setErrors({});
  };

  const handleChangeDepartment = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      programme: '',
      studyMode: '',
      researchArea: '',
      proposedChiefSupervisor: '',
      researchAreaBackup: '',
      proposedChiefSupervisorBackup: '',
    });
    formik.handleChange(e);
    await formik.setTouched({
      ...formik.touched,
      department: true,
      programme: false,
      studyMode: false,
      researchArea: false,
      proposedChiefSupervisor: false,
      researchAreaBackup: false,
      proposedChiefSupervisorBackup: false,
    });
    formik.setErrors({});
  };

  const handleSetDualOrCollaborative = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      programmeType: '',
      isDualOrCollaborative: e.target.checked,
      partner: emptyPartnerFields,
    });
  };

  const handleChangeProgramme = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      studyMode: '',
      researchArea: '',
      proposedChiefSupervisor: '',
      researchAreaBackup: '',
      proposedChiefSupervisorBackup: '',
      isDualOrCollaborative: false,
      programmeType: '',
      partner: emptyPartnerFields,
    });
    formik.handleChange(e);
    await formik.setTouched({
      ...formik.touched,
      programme: true,
      studyMode: false,
      researchArea: false,
      proposedChiefSupervisor: false,
      researchAreaBackup: false,
      proposedChiefSupervisorBackup: false,
    });
    formik.setErrors({});
  };

  const handleChangeStudyMode = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      isDualOrCollaborative: false,
      programmeType: '',
      partner: emptyPartnerFields,
    });
    formik.handleChange(e);
  };

  const handleResetPartner = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      partner: emptyPartnerFields,
    });
    formik.handleChange(e);
  };

  const handleChangeAcceptNoResearchIndicator = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setFieldValue(
      'acceptNoResearchIndicator',
      e.target.value === 'true',
    );
  };

  const handleChangeResearchAreaBackup = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const researchAreaBackupValue = e.target.value;
    await formik.setFieldValue('researchAreaBackup', researchAreaBackupValue);
    if (!researchAreaBackupValue) {
      await formik.setFieldValue('proposedChiefSupervisorBackup', null);
    }
  };

  const isDualOrCollaborative = formik.values.isDualOrCollaborative;
  const isFullTimeOrDoctor =
    formik.values.studyMode === (STUDY_MODE.FULL_TIME as '' | STUDY_MODE) &&
    formik.values.programme ===
      (PROGRAMME_NAME.DOCTOR_OF_PHILOSOPHY as '' | PROGRAMME_NAME);

  // common fields, display === true, isFullWidth === false
  const inputFieldsOne: Array<
    InputTextProps<ProgrammeChoiceFormData> & {
      display?: boolean;
      isFullWidth?: boolean;
    }
  > = [
    {
      name: 'entryYear',
      label: PROGRAMME.ENTRY_YEAR,
      type: 'select',
      options: entryYearsOptions,
      required: true,
      disabled: !enableEdit,
      display: true,
      isFullWidth: false,
      handleChange: handleChangeEntryYear,
    },
    {
      name: 'department',
      label: PROGRAMME.DEPARTMENT,
      type: 'select',
      options: departmentOptions,
      required: true,
      disabled: !enableEdit || !formik.values.entryYear,
      display: true,
      isFullWidth: false,
      handleChange: handleChangeDepartment,
    },
    {
      name: 'programme',
      label: PROGRAMME.PROGRAMME,
      type: 'select',
      options: filteredProgrammeOptions,
      required: true,
      disabled:
        !enableEdit || !formik.values.department || !formik.values.entryYear,
      display: true,
      isFullWidth: false,
      handleChange: handleChangeProgramme,
    },
    {
      name: 'studyMode',
      label: PROGRAMME.MODE_OF_STUDY,
      type: 'select',
      options: filteredStudyModeOptions,
      required: true,
      disabled:
        !enableEdit ||
        !formik.values.department ||
        !formik.values.entryYear ||
        !formik.values.programme,
      display: true,
      isFullWidth: false,
      handleChange: handleChangeStudyMode,
    },
  ];

  const noIndicateChiefSup =
    formik.values.proposedChiefSupervisor ===
      PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION ||
    !formik.values.proposedChiefSupervisor;

  const updateFormFields = async (): Promise<void> => {
    await formik.setFieldValue('proposedChiefSupervisorBackup', null);
  };

  useEffect(() => {
    if (noIndicateChiefSup) {
      // call the function
      updateFormFields()
        // make sure to catch any error
        .catch(console.error);
    }
  }, [noIndicateChiefSup]);

  const inputFieldsTwo: Array<
    InputTextProps<ProgrammeChoiceFormData> & {
      display?: boolean;
      isFullWidth?: boolean;
    }
  > = [
    {
      name: 'researchArea',
      label: PROGRAMME.RESEARCH_AREA,
      type: 'select',
      options: researchAreaOptions,
      required: true,
      disabled: !enableEdit || !formik.values.department,
      display: true,
    },
    {
      name: 'proposedChiefSupervisor',
      label: isDualOrCollaborative
        ? PROGRAMME.PROPOSED_CHIEF_SUPERVISOR
        : SupervisorTipsLabel,
      type: 'select',
      options:
        proposedChiefSupervisorOptionGroup.proposedChiefSupervisorOptions,
      required: true,
      disabled: !enableEdit || !formik.values.department,
      display: true,
    },
    {
      name: 'researchAreaBackup',
      label: PROGRAMME.RESEARCH_AREA_OPTIONAL,
      type: 'select',
      options: researchAreaOptions,
      disabled: !enableEdit || !formik.values.department,
      clearable: true,
      display: true,
      handleChange: handleChangeResearchAreaBackup,
    },
    {
      name: 'proposedChiefSupervisorBackup',
      label: isDualOrCollaborative
        ? PROGRAMME.PROPOSED_CHIEF_SUPERVISOR_OPTIONAL
        : SupervisorTipsLabel,
      type: 'select',
      // should not pass the *Not Indicated* to backend
      options:
        proposedChiefSupervisorOptionGroup.proposedChiefSupervisorBackupOptions.filter(
          sup => sup.value !== formik.values.proposedChiefSupervisor,
        ),
      disabled:
        !enableEdit ||
        !formik.values.department ||
        formik.values.researchAreaBackup === undefined ||
        noIndicateChiefSup,
      display: true,
      clearable: true,
    },
    // Fields related to dual or collaborative phd degree programme
    {
      name: 'isDualOrCollaborative',
      label: PROGRAMME.IS_DUAL_OR_COLLABORATIVE_PROGRAMME,
      type: 'checkbox',
      required: false,
      disabled: !enableEdit,
      isFullWidth: true,
      handleChange: handleSetDualOrCollaborative,
      display: isFullTimeOrDoctor,
    },
    {
      name: 'programmeType',
      label: PROGRAMME.PROGRAMME_TYPE,
      options: programmeTypeOptions.filter(
        option => option.value !== PROGRAMME_TYPE.GENERAL_PROGRAMME,
      ),
      type: 'select',
      required: true,
      disabled: !enableEdit,
      display: isDualOrCollaborative && isFullTimeOrDoctor,
      handleChange: handleResetPartner,
    },
    {
      name: 'partner.universityCategory',
      label: PROGRAMME.UNIVERSITY_CATEGORY,
      type: 'select',
      options: universityCategoryOptions,
      required: true,
      disabled: !enableEdit,
      display:
        isDualOrCollaborative &&
        formik.values.programmeType ===
          PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME &&
        isFullTimeOrDoctor,
    },
    {
      name: 'partner.isPolyUHomeUniversity',
      label: PROGRAMME.IS_POLYU_HOME,
      type: 'checkbox',
      required: false,
      disabled: !enableEdit,
      isFullWidth: true,
      display:
        formik.values.programmeType ===
          PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME &&
        formik.values.partner.universityCategory ===
          UNIVERSITY_CATEGORY.OVERSEAS &&
        isFullTimeOrDoctor,
    },
    {
      name: 'partner.university',
      label: PROGRAMME.PARTNER_INSTITUTION,
      type: 'select',
      options:
        formik.values.programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME
          ? rpgJoinPhdPartnerList
          : prgCollabPhdPartnerList,
      required: true,
      disabled: !enableEdit,
      display: isDualOrCollaborative && isFullTimeOrDoctor,
    },
    {
      name: 'partner.proposedChiefSupervisor',
      label: PROGRAMME.PROPOSED_CHIEF_SUPERVISOR_OF_PARTNER_INSTITUTION,
      type: 'text',
      required: true,
      disabled: !enableEdit,
      display: isDualOrCollaborative && isFullTimeOrDoctor,
    },
    {
      name: 'partner.partnerUniversityApplicationNo',
      label: PROGRAMME.APPLICATION_NO_OF_PARTNER_INSTITUTION,
      type: 'text',
      required: false,
      disabled: !enableEdit,
      display: isDualOrCollaborative && isFullTimeOrDoctor,
    },
  ];

  if (supervisorError ?? departmentError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Failed to load visa status.` +
            (supervisorError as Error)?.message +
            (departmentError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <Container>
      {loading && <Loading />}
      <form onSubmit={handleReadySubmit(formik)} noValidate>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container columnSpacing={4} rowSpacing={2}>
            {inputFieldsOne.map((item, index) => (
              <Grid
                key={index}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                {returnedComponent<ProgrammeChoiceFormData>({
                  item,
                  formik,
                })}
              </Grid>
            ))}

            <div
              className="px-4 pb-4 mt-2 ml-2 w-full"
              dangerouslySetInnerHTML={{
                __html: PROGRAMME.HYPER_LINK,
              }}
            />
            {inputFieldsTwo.map((item, index) => {
              const { display, isFullWidth } = item;
              return (
                display && (
                  <Grid
                    key={index}
                    size={{
                      xs: 12,
                      md: isFullWidth ? 12 : 6,
                    }}
                  >
                    {returnedComponent<ProgrammeChoiceFormData>({
                      item,
                      formik,
                    })}
                  </Grid>
                )
              );
            })}
            <Grid
              size={{
                md: 12,
              }}
            >
              <div className="text-base font-medium">
                {PROGRAMME.INTEND_TO_ACCEPT_OFFER + ' *'}
              </div>
              <RadioGroup
                name="acceptNoResearchIndicator"
                value={formik.values.acceptNoResearchIndicator}
                onChange={handleChangeAcceptNoResearchIndicator}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '2rem',
                  margin: '0.5rem',
                }}
              >
                <Radio
                  value={true}
                  label={PAGE_TEXT.YES}
                  checked={formik.values.acceptNoResearchIndicator === true}
                  disabled={!enableEdit}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                  }}
                  error={
                    formik.touched.acceptNoResearchIndicator &&
                    Boolean(formik.errors.acceptNoResearchIndicator)
                  }
                />
                <Radio
                  value={false}
                  label={PAGE_TEXT.NO}
                  checked={formik.values.acceptNoResearchIndicator === false}
                  disabled={!enableEdit}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                  }}
                  error={
                    formik.touched.acceptNoResearchIndicator &&
                    Boolean(formik.errors.acceptNoResearchIndicator)
                  }
                />
              </RadioGroup>
            </Grid>
          </Grid>
        </Box>
        {enableEdit && (
          <Box display={'flex'} justifyContent={'center'} marginTop={2}>
            <Button type="submit" variant="contained">
              {location.applicationId !== undefined
                ? PAGE_TEXT.BUTTON.SAVE
                : PAGE_TEXT.CREATE}
            </Button>
          </Box>
        )}
      </form>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  margin-top: 35px;
  width: 100%;

  .MuiGrid-item {
    width: 100%;
  }

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }
`;
